import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Check, DollarSign, ArrowDownLeft, ArrowUpRight, CreditCard, Building2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { baseURL } from '@/config/APIConfig';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API_URL = baseURL.AiBaseURL;

const PAYMENT_METHODS = [
    { value: 'check', label: 'Check' },
    { value: 'wire', label: 'Wire Transfer' },
    { value: 'ach', label: 'ACH' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'cash', label: 'Cash' },
];

const Payments = () => {
    const { t } = useLanguage();
    const [payments, setPayments] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [bills, setBills] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [showModal, setShowModal] = useState(false);
    
    const [formData, setFormData] = useState({
        payment_type: 'receipt',
        payment_date: new Date().toISOString().split('T')[0],
        party_name: '',
        bank_account_id: '',
        currency_code: 'USD',
        amount: 0,
        payment_method: 'check',
        reference_no: '',
        notes: '',
        applications: []
    });

    useEffect(() => {
        fetchPayments();
        fetchInvoices();
        fetchBills();
        fetchBankAccounts();
    }, [filterType]);

    const fetchPayments = async () => {
        try {
            let url = `${API_URL}/api/accounting/payments?`;
            if (filterType) url += `payment_type=${filterType}&`;
            
            const response = await fetch(url);
            const data = await response.json();
            setPayments(data);
        } catch (error) {
            toast.error('Failed to load payments');
        } finally {
            setLoading(false);
        }
    };

    const fetchInvoices = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/invoices?status=issued`);
            const data = await response.json();
            setInvoices(data.filter(inv => inv.balance_due > 0));
        } catch (error) {
            console.error('Error fetching invoices:', error);
        }
    };

    const fetchBills = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/bills?status=approved`);
            const data = await response.json();
            setBills(data.filter(bill => bill.balance_due > 0));
        } catch (error) {
            console.error('Error fetching bills:', error);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/chart-of-accounts?is_bank_account=true`);
            const data = await response.json();
            setBankAccounts(data);
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate amount
        if (formData.amount <= 0) {
            toast.error('Amount must be greater than zero');
            return;
        }
        
        try {
            const response = await fetch(`${API_URL}/api/accounting/payments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to save payment');
            }
            
            toast.success('Payment created!');
            setShowModal(false);
            resetForm();
            fetchPayments();
            fetchInvoices();
            fetchBills();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handlePost = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/accounting/payments/${id}/post`, { method: 'POST' });
            if (!response.ok) throw new Error('Failed to post');
            toast.success('Payment posted!');
            fetchPayments();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this payment?')) return;
        try {
            const response = await fetch(`${API_URL}/api/accounting/payments/${id}`, { method: 'DELETE' });
            if (!response.ok) throw new Error('Failed to delete');
            toast.success('Payment deleted!');
            fetchPayments();
            fetchInvoices();
            fetchBills();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const resetForm = () => {
        setFormData({
            payment_type: 'receipt',
            payment_date: new Date().toISOString().split('T')[0],
            party_name: '',
            bank_account_id: '',
            currency_code: 'USD',
            amount: 0,
            payment_method: 'check',
            reference_no: '',
            notes: '',
            applications: []
        });
    };

    const toggleApplication = (type, doc) => {
        const existing = formData.applications.find(a => 
            (type === 'invoice' && a.invoice_id === doc.id) ||
            (type === 'bill' && a.bill_id === doc.id)
        );
        
        if (existing) {
            setFormData({
                ...formData,
                applications: formData.applications.filter(a => a !== existing)
            });
        } else {
            setFormData({
                ...formData,
                applications: [
                    ...formData.applications,
                    {
                        invoice_id: type === 'invoice' ? doc.id : null,
                        bill_id: type === 'bill' ? doc.id : null,
                        applied_amount: Math.min(doc.balance_due, formData.amount - getTotalApplied())
                    }
                ]
            });
        }
    };

    const updateApplicationAmount = (index, amount) => {
        const newApps = [...formData.applications];
        newApps[index].applied_amount = parseFloat(amount) || 0;
        setFormData({ ...formData, applications: newApps });
    };

    const getTotalApplied = () => {
        return formData.applications.reduce((sum, a) => sum + (a.applied_amount || 0), 0);
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

    const totalReceipts = payments.filter(p => p.payment_type === 'receipt').reduce((sum, p) => sum + (p.amount || 0), 0);
    const totalPayments = payments.filter(p => p.payment_type === 'payment').reduce((sum, p) => sum + (p.amount || 0), 0);

    return (
        <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-800 via-emerald-700 to-teal-700 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                            <DollarSign className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{t('payments')}</h1>
                            <p className="text-emerald-200">{t('managePayments')}</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            onClick={() => { resetForm(); setFormData(f => ({...f, payment_type: 'receipt'})); setShowModal(true); }}
                            className="bg-green-500 hover:bg-green-600 text-white"
                            data-testid="receive-payment-btn"
                        >
                            <ArrowDownLeft className="w-4 h-4 mr-2" /> {t('receipt')}
                        </Button>
                        <Button 
                            onClick={() => { resetForm(); setFormData(f => ({...f, payment_type: 'payment'})); setShowModal(true); }}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="make-payment-btn"
                        >
                            <ArrowUpRight className="w-4 h-4 mr-2" /> {t('payment')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-slate-500">
                    <p className="text-sm text-slate-500">Total Transactions</p>
                    <p className="text-2xl font-bold">{payments.length}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-green-500">
                    <p className="text-sm text-slate-500">Total Received</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(totalReceipts)}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-orange-500">
                    <p className="text-sm text-slate-500">Total Paid</p>
                    <p className="text-xl font-bold text-orange-600">{formatCurrency(totalPayments)}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-blue-500">
                    <p className="text-sm text-slate-500">Net Cash Flow</p>
                    <p className={`text-xl font-bold ${totalReceipts - totalPayments >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(totalReceipts - totalPayments)}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 items-center bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
                <div className="flex-1 max-w-md">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                    </div>
                </div>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
                    <option value="">All Types</option>
                    <option value="receipt">Receipts</option>
                    <option value="payment">Payments</option>
                </select>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="bg-slate-100 dark:bg-slate-700">
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Payment #</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Date</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Party</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Method</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Reference</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Amount</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Status</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {loading ? (
                            <tr><td colSpan={9} className="px-4 py-8 text-center">{t('loading')}</td></tr>
                        ) : payments.length === 0 ? (
                            <tr><td colSpan={9} className="px-4 py-8 text-center">{t('noPaymentsFound')}</td></tr>
                        ) : payments.filter(p => 
                            !searchTerm || 
                            p.payment_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            p.party_name?.toLowerCase().includes(searchTerm.toLowerCase())
                        ).map((payment) => (
                            <tr key={payment.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                <td className="px-4 py-3 text-sm font-medium text-emerald-600">{payment.payment_no}</td>
                                <td className="px-4 py-3 text-sm">{formatDate(payment.payment_date)}</td>
                                <td className="px-4 py-3 text-center">
                                    {payment.payment_type === 'receipt' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                            <ArrowDownLeft className="w-3 h-3" /> Receipt
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                            <ArrowUpRight className="w-3 h-3" /> Payment
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-sm">{payment.party_name || '-'}</td>
                                <td className="px-4 py-3 text-sm capitalize">{payment.payment_method || '-'}</td>
                                <td className="px-4 py-3 text-sm">{payment.reference_no || '-'}</td>
                                <td className="px-4 py-3 text-sm text-right font-bold">{formatCurrency(payment.amount)}</td>
                                <td className="px-4 py-3 text-center">
                                    {payment.status === 'posted' ? (
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Posted</span>
                                    ) : (
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Draft</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-center gap-1">
                                        {payment.status !== 'posted' && (
                                            <>
                                                <button onClick={() => handlePost(payment.id)} className="p-1.5 rounded-lg hover:bg-green-100 text-green-600" title="Post"><Check className="w-4 h-4" /></button>
                                                <button onClick={() => handleDelete(payment.id)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className={`p-6 border-b flex items-center justify-between ${formData.payment_type === 'receipt' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-orange-50 dark:bg-orange-900/20'}`}>
                            <div className="flex items-center gap-3">
                                {formData.payment_type === 'receipt' ? (
                                    <ArrowDownLeft className="w-6 h-6 text-green-600" />
                                ) : (
                                    <ArrowUpRight className="w-6 h-6 text-orange-600" />
                                )}
                                <h2 className="text-xl font-bold">
                                    {formData.payment_type === 'receipt' ? 'Receive Payment' : 'Make Payment'}
                                </h2>
                            </div>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Payment Date *</label>
                                    <Input type="date" value={formData.payment_date} onChange={(e) => setFormData({...formData, payment_date: e.target.value})} required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        {formData.payment_type === 'receipt' ? 'Customer' : 'Vendor'} *
                                    </label>
                                    <Input value={formData.party_name} onChange={(e) => setFormData({...formData, party_name: e.target.value})} required placeholder="Enter name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Amount *</label>
                                    <Input type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})} required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Payment Method</label>
                                    <select value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
                                        {PAYMENT_METHODS.map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Bank Account</label>
                                    <select value={formData.bank_account_id} onChange={(e) => setFormData({...formData, bank_account_id: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
                                        <option value="">Select Bank Account...</option>
                                        {bankAccounts.map(acc => (
                                            <option key={acc.id} value={acc.id}>{acc.account_code} - {acc.account_name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Reference #</label>
                                    <Input value={formData.reference_no} onChange={(e) => setFormData({...formData, reference_no: e.target.value})} placeholder="Check #, Wire Ref, etc." />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Notes</label>
                                <textarea 
                                    value={formData.notes} 
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})} 
                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                    rows={2}
                                />
                            </div>

                            {/* Apply to Invoices/Bills */}
                            {formData.amount > 0 && (
                                <div className="space-y-4">
                                    <h3 className="text-sm font-semibold">
                                        Apply to {formData.payment_type === 'receipt' ? 'Invoices' : 'Bills'}
                                    </h3>
                                    <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 max-h-60 overflow-y-auto space-y-2">
                                        {(formData.payment_type === 'receipt' ? invoices : bills).length === 0 ? (
                                            <p className="text-slate-500 text-sm text-center py-4">No outstanding {formData.payment_type === 'receipt' ? 'invoices' : 'bills'}</p>
                                        ) : (
                                            (formData.payment_type === 'receipt' ? invoices : bills).map((doc) => {
                                                const type = formData.payment_type === 'receipt' ? 'invoice' : 'bill';
                                                const isSelected = formData.applications.find(a => 
                                                    (type === 'invoice' && a.invoice_id === doc.id) ||
                                                    (type === 'bill' && a.bill_id === doc.id)
                                                );
                                                const appIndex = formData.applications.findIndex(a => 
                                                    (type === 'invoice' && a.invoice_id === doc.id) ||
                                                    (type === 'bill' && a.bill_id === doc.id)
                                                );
                                                
                                                return (
                                                    <div key={doc.id} className={`flex items-center justify-between p-3 rounded-lg border ${isSelected ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                                                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={!!isSelected}
                                                                onChange={() => toggleApplication(type, doc)}
                                                                className="w-4 h-4 rounded"
                                                            />
                                                            <div>
                                                                <p className="font-medium text-sm">{doc.invoice_no || doc.bill_no}</p>
                                                                <p className="text-xs text-slate-500">{doc.customer_name || doc.vendor_name}</p>
                                                            </div>
                                                        </label>
                                                        <div className="text-right">
                                                            <p className="text-sm font-medium">Balance: {formatCurrency(doc.balance_due)}</p>
                                                            {isSelected && (
                                                                <Input 
                                                                    type="number" 
                                                                    step="0.01" 
                                                                    min="0" 
                                                                    max={doc.balance_due}
                                                                    value={formData.applications[appIndex]?.applied_amount || 0}
                                                                    onChange={(e) => updateApplicationAmount(appIndex, e.target.value)}
                                                                    className="w-24 text-right text-sm mt-1"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                    
                                    <div className="flex justify-between items-center text-sm">
                                        <span>Payment Amount: <strong>{formatCurrency(formData.amount)}</strong></span>
                                        <span>Applied: <strong>{formatCurrency(getTotalApplied())}</strong></span>
                                        <span>Unapplied: <strong className="text-amber-600">{formatCurrency(formData.amount - getTotalApplied())}</strong></span>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>{t('cancel')}</Button>
                                <Button 
                                    type="submit" 
                                    className={formData.payment_type === 'receipt' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-orange-600 hover:bg-orange-700 text-white'}
                                >
                                    {t('create')} {formData.payment_type === 'receipt' ? t('receipt') : t('payment')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Payments;
